import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { login as loginAPI } from "../../Api/auth";
import { useAuth } from "../../AuthContext";
import FormError from "./FormError"; 


const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    // Reset error on change
    setErrors((prev) => ({
      ...prev,
      [e.target.name]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    toast.dismiss();
    setErrors({ email: "", password: "" });

    let hasError = false;
    const newErrors = { email: "", password: "" };

    if (!formData.email) {
      const msg = t("login.emailRequired");
      toast.error(msg);
      newErrors.email = msg;
      hasError = true;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        const msg = t("login.invalidEmail");
        toast.error(msg);
        newErrors.email = msg;
        hasError = true;
      }
    }

    if (!formData.password) {
      const msg = t("login.passwordRequired");
      toast.error(msg);
      newErrors.password = msg;
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const response = await loginAPI(formData);
      if (response.data) {
        login(response.data);
        window.dispatchEvent(new Event("user-logged-in"));


        const successMessage =
          response?.detail ||
          response?.message ||
          t("login.successMessage");

        toast.success(successMessage);

        setTimeout(() => {
          navigate(`/profile-edit`);
        }, 3000);
      }
    } catch (error) {
      const errorMessage =
        error.detail || error.title || error.message || t("login.serverError");

      toast.error(errorMessage);

      if (errorMessage === "EmailNotConfirmed") {
        setTimeout(() => {
          navigate("/confirm-email", { state: { email: formData.email } });
        }, 3000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="rbt-header-sticky rbt-header-transpernt-active active-light-mode">
      <section className="py-5">
        <div className="container py-4">
          <div className="row justify-content-center">
            <div className="col-lg-5">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-4 p-md-5">
                  <h2 className="text-center fw-bold text-primary-custom mb-4">
                    {t("login.title")}
                  </h2>
                  <p className="text-muted text-center mb-4">
                    {t("login.subtitle")}
                  </p>

                  <form onSubmit={handleSubmit} noValidate>
                    <div className="mb-4">
                      <div className="form-floating">
                        <input
                          type="email"
                          className={`form-control ${errors.email ? "is-invalid" : ""}`}
                          id="email"
                          name="email"
                          placeholder={t("login.email")}
                          value={formData.email}
                          onChange={handleChange}
                        />
                        <label htmlFor="email">{t("login.email")}</label>
                      </div>
                      <FormError errorMessage={errors.email} />
                    </div>

                    <div className="mb-3">
                      <div className="form-floating">
                        <input
                          type="password"
                          className={`form-control ${errors.password ? "is-invalid" : ""}`}
                          id="password"
                          name="password"
                          placeholder={t("login.password")}
                          value={formData.password}
                          onChange={handleChange}
                        />
                        <label htmlFor="password">{t("login.password")}</label>
                      </div>
                      <FormError errorMessage={errors.password} />
                    </div>

                    <div className="d-flex justify-content-between mb-4">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="rememberMe" />
                        <label className="form-check-label" htmlFor="rememberMe">
                          {t("login.rememberMe")}
                        </label>
                      </div>
                      <a href="/reset-password" className="text-decoration-none">
                        {t("login.forgotPassword")}
                      </a>
                    </div>

                    <div className="mb-4">
                      <button type="submit" className="btn btn-outline-primary w-100 py-3" disabled={isLoading}>
                        {isLoading ? t("login.loading") : t("login.submit")}
                      </button>
                    </div>

                    <div className="text-center mb-4">
                      <p className="mb-0">
                        {t("login.noAccount")}{" "}
                        <a href="/signup" className="text-decoration-none">
                          {t("login.signup")}
                        </a>
                      </p>
                    </div>

                    {/* <div className="divider d-flex align-items-center my-4">
                      <span className="divider-text">{t("login.or")}</span>
                    </div> */}

                    {/* <div className="social-login">
                      <button type="button" className="btn btn-outline-secondary w-100 mb-3">
                        <i className="fab fa-google me-2"></i> {t("login.loginWithGoogle")}
                      </button>
                      <button type="button" className="btn btn-outline-secondary w-100">
                        <i className="fab fa-apple me-2"></i> {t("login.loginWithApple")}
                      </button>
                    </div> */}
                  </form>

                  <ToastContainer />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
